/**
 * ClassIcon.jsx — Hand-drawn SVG icon per class type
 *
 * Used on class cards (ClassGrid) and class detail pages (BookingForm).
 * Each icon is a 32×32 line-art SVG matching the class subject.
 *
 * Props:
 *   classId {number} — matches the class id from CLASSES data
 *   color   {string} — stroke color (defaults to gold #9C7A3C)
 *   size    {number} — width/height in px (defaults to 30)
 */
export default function ClassIcon({ classId, color = '#9C7A3C', size = 30 }) {
  const svgProps = {
    width: size,
    height: size,
    viewBox: '0 0 32 32',
    fill: 'none',
    stroke: color,
    strokeWidth: 1.6,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    style: { display: 'block', flexShrink: 0 },
  };

  if (classId === 1) {
    // Matcha Bowl — wide shallow chawan bowl from front, bamboo whisk leaning in
    return (
      <svg {...svgProps}>
        {/* Wide shallow bowl body */}
        <path d="M4 18 Q5 26 16 27 Q27 26 28 18" />
        {/* Flat wide rim */}
        <line x1="3" y1="18" x2="29" y2="18" />
        {/* Small footing ring */}
        <line x1="12" y1="27" x2="20" y2="27" />
        {/* Bamboo whisk handle */}
        <line x1="22" y1="9" x2="18" y2="18" />
        {/* Whisk tines fanning out */}
        <path d="M22 9 Q25 5 24 3" />
        <path d="M22 9 Q26 7 27 5" />
        <path d="M22 9 Q24 11 25 8" />
      </svg>
    );
  }

  if (classId === 2) {
    // Wheel Throwing — clay vessel rising from spinning wheel, side view
    return (
      <svg {...svgProps}>
        {/* Wheel platform ellipse */}
        <ellipse cx="16" cy="24" rx="12" ry="3" />
        {/* Clay vessel forming on wheel */}
        <path d="M11 24 Q10 17 13 12 Q16 9 19 12 Q22 17 21 24" />
        {/* Open rim of vessel */}
        <path d="M13 12 Q16 10 19 12" />
        {/* Motion arc suggesting spin */}
        <path d="M4 24 Q4 18 8 14" />
      </svg>
    );
  }

  if (classId === 4) {
    // Hand Built Planter — terracotta pot with sprouting leaves
    return (
      <svg {...svgProps}>
        {/* Pot rim band */}
        <path d="M8 13 Q8 11 10 11 L22 11 Q24 11 24 13 L23 14 L9 14 Z" />
        {/* Pot body */}
        <path d="M9 14 L8 26 L24 26 L23 14" />
        {/* Stem */}
        <line x1="16" y1="11" x2="16" y2="5" />
        {/* Right leaf */}
        <path d="M16 7 Q22 5 21 1 Q15 4 16 7" />
        {/* Left leaf */}
        <path d="M16 7 Q10 5 11 1 Q17 4 16 7" />
      </svg>
    );
  }

  if (classId === 5) {
    // Hand Built Vase — tall elegant vase, narrow neck, wide belly
    return (
      <svg {...svgProps}>
        {/* Vase silhouette */}
        <path d="M14 28 Q9 28 8 22 Q7 16 11 12 Q12 9 12 6 L14 4 L18 4 L20 6 Q20 9 21 12 Q25 16 24 22 Q23 28 18 28 Z" />
        {/* Mouth opening */}
        <line x1="14" y1="4" x2="18" y2="4" />
        {/* Belly accent curve */}
        <path d="M10 20 Q16 23 22 20" />
      </svg>
    );
  }

  if (classId === 6) {
    // Pottery Party — clay pot centre with celebration sparkles
    return (
      <svg {...svgProps}>
        {/* Central pot */}
        <path d="M11 10 L10 23 L22 23 L21 10 Z" />
        <line x1="10" y1="10" x2="22" y2="10" />
        {/* Base */}
        <path d="M9 23 L9 26 L23 26 L23 23" />
        {/* Cross sparkle left */}
        <line x1="4" y1="5" x2="4" y2="9" />
        <line x1="2" y1="7" x2="6" y2="7" />
        {/* Cross sparkle right */}
        <line x1="28" y1="7" x2="28" y2="11" />
        <line x1="26" y1="9" x2="30" y2="9" />
        {/* Dot accents */}
        <circle cx="8" cy="3" r="1" fill={color} stroke="none" />
        <circle cx="24" cy="4" r="1" fill={color} stroke="none" />
        <circle cx="16" cy="2" r="1" fill={color} stroke="none" />
      </svg>
    );
  }

  if (classId === 7) {
    // Hand Built Mug — clean mug body, D-handle, two steam curls
    return (
      <svg {...svgProps}>
        {/* Mug body */}
        <path d="M6 12 L6 25 Q6 27 8 27 L20 27 Q22 27 22 25 L22 12 Z" />
        {/* Rim */}
        <line x1="5" y1="12" x2="23" y2="12" />
        {/* D-shaped handle */}
        <path d="M22 16 Q29 16 29 20 Q29 24 22 24" />
        {/* Steam curls */}
        <path d="M10 8 C9 5 11 4 10 1" />
        <path d="M16 8 C15 5 17 4 16 1" />
      </svg>
    );
  }

  if (classId === 8) {
    // Oaxaca Inspired Vase Decoration — bulbous vase with geometric pattern band
    return (
      <svg {...svgProps}>
        {/* Vase neck — narrow at top */}
        <line x1="11" y1="8" x2="11" y2="12" />
        <line x1="21" y1="8" x2="21" y2="12" />
        {/* Vase rim */}
        <line x1="10" y1="8" x2="22" y2="8" />
        {/* Vase body — bulbous middle */}
        <path d="M11 12 Q11 20 16 24 Q21 20 21 12" />
        {/* Base ring */}
        <path d="M10 24 Q10 26 16 26 Q22 26 22 24" />
        {/* Oaxacan geometric pattern band — triangles and diamonds */}
        <line x1="12" y1="16" x2="20" y2="16" />
        <path d="M12 16 L14 20 L16 16 M16 16 L18 20 L20 16" />
        {/* Accent dots */}
        <circle cx="13" cy="19" r="0.8" fill={color} stroke="none" />
        <circle cx="19" cy="19" r="0.8" fill={color} stroke="none" />
      </svg>
    );
  }

  // id === 7: Hand Built Mug — clean mug body, D-handle, two steam curls (default)
  return (
    <svg {...svgProps}>
      {/* Mug body */}
      <path d="M6 12 L6 25 Q6 27 8 27 L20 27 Q22 27 22 25 L22 12 Z" />
      {/* Rim */}
      <line x1="5" y1="12" x2="23" y2="12" />
      {/* D-shaped handle */}
      <path d="M22 16 Q29 16 29 20 Q29 24 22 24" />
      {/* Steam curls */}
      <path d="M10 8 C9 5 11 4 10 1" />
      <path d="M16 8 C15 5 17 4 16 1" />
    </svg>
  );
}
